import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  output,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { firstValueFrom } from 'rxjs';
import { Table } from 'src/app/models/ui/table';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { Base64Service } from 'src/app/services/base64/base64.service';
import { WebSocketService } from 'src/app/services/Websocket/websocket.service';
import { ProgressGroupStatus, ProgressStatus, ProgressEvent } from 'src/app/types/progress';

@Component({
  selector: 'app-zaehler-import',
  templateUrl: './zaehler-import.component.html',
  styleUrl: './zaehler-import.component.scss',
  imports: [CommonModule, ClarityModule, FormsModule, MrTranslatePipe],
  providers: [WebSocketService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZaehlerImportComponent {
  readonly modalOpen = model(true);
  readonly otypid = input.required<number>();
  readonly refresh = output<void>();

  private readonly api = inject(ZaehlerApiService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);
  private readonly fileLoader = inject(Base64Service);
  private readonly webSocket = inject(WebSocketService);

  private readonly filesModel = viewChild.required('filesInput', { read: NgModel });

  protected readonly progressGroup = signal<ProgressGroupStatus>({ status: 'idle' });
  protected readonly files = signal<FileList>(null);

  protected readonly jsonResource = resource({
    params: () => (this.filesModel().valid ? this.files()?.[0] : undefined),
    loader: ({ params }) => this.fileLoader.getTextStringFromFile(params),
  });

  constructor() {
    this.webSocket._config.name = 'Zaehler Import Progress';
    this.webSocket.startCon();
    this.webSocket.registerHandler('taskStarted', ({ id, title, total }: ProgressEvent) => {
      this.progressGroup.update(group => {
        const initTask: ProgressStatus = { total, title, percent: 0 };
        if (group.status == 'idle') return { status: 'pending', children: { [id]: initTask } };
        else {
          group.children[id] = initTask;
          return { ...group };
        }
      });
    });
    this.webSocket.registerHandler('taskProgressChanged', ({ id, current }: ProgressEvent) => {
      this.progressGroup.update(group => {
        if (group.status != 'idle') {
          let child = group.children[id];
          child = { ...child, current, percent: Math.floor((+current / child.total) * 100) };
          group.children = { ...group.children, [id]: child };
        }
        return { ...group };
      });
    });
    this.webSocket.registerHandler('taskErrored', ({ message }: ProgressEvent) => {
      console.log('taskErrored', message);
      this.progressGroup.update(group => ({ ...group, status: 'error', children: null }));
    });
    this.webSocket.registerHandler('taskEnded', ({ id }: ProgressEvent) => {
      const group = this.progressGroup();
      if (group.status == 'idle') return;
      else {
        setTimeout(
          () =>
            this.progressGroup.update(group => {
              if (group.status != 'idle') {
                delete group.children[id];
                if (!Object.keys(group.children).length) {
                  this.filesModel().reset();
                  this.filesModel().control.markAsUntouched();
                  return { status: 'done', children: null };
                }
              }
              return { ...group };
            }),
          Object.keys(group.children).length == 1 ? 3000 : 100
        );
      }
    });
  }

  protected async startImport() {
    if (!this.jsonResource.hasValue()) return;

    const sendObj: Record<string, Table> = JSON.parse(this.jsonResource.value());
    if (!sendObj.szaehn || !sendObj.szaehn.columns || !sendObj.szaehn.rows) {
      this.toastr.error(this.mrTranslate.transform('Kein gültigen Zählerprogrammen-Export!'));
      this.filesModel().control.setErrors({ accept: '.json' });
      return;
    }
    const { success } = await this.api.importZaehler(this.webSocket._id(), this.otypid(), sendObj);

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.refresh.emit();
    } else this.toastr.error(this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
  }
}
